import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Grid, Button, Tooltip, Divider, IconButton } from '@mui/material'
import { Typography } from '@mui/material_modern'
import { useTheme } from '@mui/material/styles'
import HorizontalGrid from 'components/generic/grid/HorizontalGrid'

import SelectButton from './SelectButton'
import SplitButton from './SplitButton'

export const ACTION_TYPE = {
  SelectButton: 'select-button',
  ActionButton: 'action-button',
  SplitButton: 'split-button',
  TooltipButton: 'tooltip-button',
  IconButtonGroup: 'icon-button-group',
  Divider: 'divider',
  Button: 'button',
  Text: 'text',
}

const ButtonList = ({ buttonsData }) => {
  const navigate = useNavigate()

  const handleNavigate = button => () => button.onClick(navigate)

  const handleSelect = button => option => button.onClick(option)

  const theme = useTheme()

  const defaultButtonProps = {
    size: 'small',
    variant: 'contained',
  }

  return (
    <HorizontalGrid>
      {buttonsData?.map(button => {
        switch (button.type) {
          case ACTION_TYPE.SelectButton:
            return (
              <Grid item key={button.id}>
                <SelectButton
                  {...defaultButtonProps}
                  {...button}
                  onClick={handleSelect(button)}
                  startIcon={button.icon}
                />
              </Grid>
            )
          case ACTION_TYPE.ActionButton:
            return (
              <Grid item key={button.id}>
                <Button {...defaultButtonProps} {...button} startIcon={button.icon}>
                  {button.text}
                </Button>
              </Grid>
            )
          case ACTION_TYPE.SplitButton:
            return (
              <Grid item key={button.id}>
                <SplitButton {...defaultButtonProps} {...button} startIcon={button.icon}>
                  {button.text}
                </SplitButton>
              </Grid>
            )
          case ACTION_TYPE.TooltipButton:
            return (
              <Grid item key={button.id}>
                <Tooltip {...(button.tooltipProps || {})}>
                  <div>
                    <Button
                      {...defaultButtonProps}
                      {...button}
                      onClick={handleNavigate(button)}
                      startIcon={button.icon}
                    >
                      {button.text}
                    </Button>
                  </div>
                </Tooltip>
              </Grid>
            )
          case ACTION_TYPE.IconButtonGroup:
            return (
              <Grid
                item
                key={button.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '46.75px', marginX: '8px' }}
              >
                {button.label && (
                  <Typography flexItem variant="body2" color="text.secondary">
                    {button.label}
                  </Typography>
                )}
                <div
                  style={{
                    borderRadius: '4px',
                    outline: `1px solid ${theme.palette.borderColor.default}`,
                    padding: '0px 4px',
                  }}
                >
                  <HorizontalGrid>
                    {button.icons &&
                      button.icons.map((iconButton, index) => (
                        <Tooltip key={index} {...(iconButton.tooltipProps || {})}>
                          <Grid item>
                            <IconButton
                              {...defaultButtonProps}
                              {...iconButton}
                              size="small"
                              sx={{
                                color: theme.palette.primary.main,
                              }}
                              onClick={iconButton.onClick ? iconButton.onClick : handleNavigate(button)}
                            >
                              {iconButton.icon}
                            </IconButton>
                          </Grid>
                        </Tooltip>
                      ))}
                  </HorizontalGrid>
                </div>
              </Grid>
            )
          case ACTION_TYPE.Text:
            return (
              <Grid item key={button.id}>
                <Typography {...button}>{button.text}</Typography>
              </Grid>
            )
          case ACTION_TYPE.Divider:
            return (
              <Grid item key={button.id}>
                <Divider orientation="vertical" flexItem sx={{ height: '16px', margin: '0px 8px' }} />
              </Grid>
            )
          case ACTION_TYPE.Button:
          default:
            return (
              <Grid item key={button.id}>
                <Button {...defaultButtonProps} {...button} onClick={handleNavigate(button)} startIcon={button.icon}>
                  {button.text}
                </Button>
              </Grid>
            )
        }
      })}
    </HorizontalGrid>
  )
}

ButtonList.propTypes = {
  buttonsData: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      type: PropTypes.string,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      onClick: PropTypes.func.isRequired,
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
}

export default ButtonList
